<template src="./answer.html"></template>

<script lang="ts">
import { nextTick } from 'vue'
import { RestError } from 'services/api-service'
import flagAnswerService from 'services/api/flag-answer'
import voteAnswerService from 'services/api/vote-answer'
import domUtils from 'util/dom-utils'
import formatName from 'filters/format-name'
import AnswerReplyForm from 'components/answers/AnswerReplyForm.vue'
import Byline from 'objects/byline/Byline.vue'
import { getOfflineOrUnexpectedMsg } from 'services/msg'
import globalStore from 'init/global-store'
import { ISubmissionContext } from './answer-reply-submission-context'
import ProductAbout from 'components/product-about/ProductAbout.vue'
import Reply from 'components/reply/Reply.vue'
import { getSessionVar, setSessionVar, removeSessionVar, ANSWER_VOTED_TYPE }
  from 'util/session-storage'
import trackingService from 'services/tracking-service'

export default {
  name: 'Answer',
  data() {
    return {
      globalStoreState: globalStore.state,
      showReplyForm: false,
      showAllReplies: false,
      wasFlagged: false,
      wasVotedOn: false,
      voteBeingProcessed: false
    }
  },
  props: {
    answer: Object,
    isBestAnswer: Boolean,
    isOnlyAnswer: Boolean, // only answer for the question
    originSiteKey: String
  },
  inject: {
    soloQuestionCtx: {
      from: 'soloQuestionCtx',
      default: {}
    },
    submissionCtx: { // ISubmissionContext
      from: 'submissionCtx'
    }
  },
  provide() {
    return {
      answerId: this.answer.id
    }
  },
  components: {
    'tt-byline': Byline,
    'tt-product-about': ProductAbout,
    'tt-reply': Reply,
    'tt-reply-form': AnswerReplyForm
  },
  computed: {
    canVote() {
      // can't vote if vote submission is in progress or the current user wrote this answer
      const loggedInUser = this.globalStoreState.user
      return !this.voteBeingProcessed &&
        !(loggedInUser && loggedInUser.id && this.answer.user && this.answer.user.id &&
          loggedInUser.id === this.answer.user.id)
    },
    controlsId() {
      // id for aria-controls on reply button.  id is on reply form
      return `tt-ansreply-form-${this.answer.id}`
    },
    menuItems() {
      const flagIcon = this.wasFlagged ? 'flag-on' : 'flag-off'
      let flagText = this.$msg('answer.inaccurate')
      let flagAriaLabel = this.$msg('answer.inaccurate.aria', [this.username])
      if (this.wasFlagged) {
        flagText = this.$msg('answer.flagged')
        flagAriaLabel = this.$msg('answer.flagged.aria', [this.username])
      }
      return [
        { name: 'reply', title: this.$msg('answer.reply'), iconName: 'reply',
          cntrlsId: this.controlsId, expanded: this.showReplyForm.toString() },
        { name: 'flag', title: flagText, iconName: flagIcon, disabled: this.wasFlagged,
          ariaLabel: flagAriaLabel }
      ]
    },
    nestedReplies() {
      if (this.isBestAnswer && !this.isOnlyAnswer) {
        return []
      }
      if (this.showAllReplies || this.answer.replies.length <= 3) {
        return this.answer.replies
      }
      return this.answer.replies.slice(0, 3)
    },
    showActions() {
      return !this.isBestAnswer && this.answer.replies.length > 3
    },
    username() {
      return formatName(this.answer.user)
    },
    voteIconName() {
      return this.wasVotedOn ? 'helpful-on' : 'helpful-off'
    }
  },
  methods: {
    closeForm() {
      this.showReplyForm = false
      // TT-11853 - should focus back on "Reply", but complicated by mobile.
      this.focusOnBylineItem('reply')
    },
    showReplies(show) {
      this.showAllReplies = show
      trackingService.queueEvent('qa', 'show-replies', this.answer.catItem.sku)
    },
    menuItemSelected(menuItem) {
      if (menuItem.name === 'flag') {
        this.flag()
      } else if (menuItem.name === 'reply') {
        this.showReplyForm = true
      }
    },
    flag() {
      if (!this.wasFlagged) {
        this.wasFlagged = true
        flagAnswerService.flagAnswer({
          id: this.answer.id,
          originSiteKey: this.originSiteKey || '',
          sku: this.answer.catItem.sku
        }).catch((e: RestError) => {
          this.showError(e)
        }).then(() => {
          trackingService.queueEvent('qa', 'flag', this.answer.catItem.sku,
            { aid: this.answer.id })
        })
        nextTick(() => this.focusOnBylineItem('flag'))
      }
    },
    focusOnBylineItem(name) {
      const bylineEl = this.$refs.byline.$el
      // tries to focus on desktop first, then on mobile if not found
      domUtils.focus(() => bylineEl.querySelector(`.tt-o-byline__options-item--${name}`))
        .catch(() => domUtils.safeFocus(() => bylineEl.querySelector('.tt-o-byline__menu-trigger')))
    },
    showError(e: RestError) {
      (this.submissionCtx as ISubmissionContext).showError(
        getOfflineOrUnexpectedMsg(e), false, true)
      this.isSubmitting = false
    },
    vote() {
      if (this.voteBeingProcessed) {
        return
      }
      this.voteBeingProcessed = true
      const up = this.wasVotedOn ? -1 : 1
      voteAnswerService.voteAnswer({
        id: this.answer.id,
        originSiteKey: this.originSiteKey || '',
        sku: this.answer.catItem.sku
      }, {
        up
      }).then(() => {
        this.wasVotedOn = !this.wasVotedOn
        if (this.wasVotedOn) {
          setSessionVar(ANSWER_VOTED_TYPE, this.answer.id, this.wasVotedOn)
        } else {
          removeSessionVar(ANSWER_VOTED_TYPE, this.answer.id)
        }
        this.voteBeingProcessed = false
        trackingService.queueEvent('qa', 'vote-answer', this.answer.catItem.sku,
          { aid: this.answer.id, vote: up })
      }).catch((e: RestError) => {
        this.showError(e)
        this.voteBeingProcessed = false
      })
    }
  },
  created() {
    this.showReplyForm = this.soloQuestionCtx.value?.openReplyForAnswerId === this.answer.id
    this.wasVotedOn = getSessionVar(ANSWER_VOTED_TYPE, this.answer.id) ? true : false
  }
}
</script>

<style lang="scss" src="./answer.scss"></style>
